import {
  Box,
  Card,
  Chip,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { QuestionCategory } from "database";
import { useLocales } from "../../locales";
import { getCategoryTooltips, CATEGORY_ICONS } from "../stepper/CustomizedStepper";

type GoalData = {
  id: string;
  targetRating: number;
  targetDate: Date;
  dateCompleted?: Date | null;
  goalObjective: {
    id: string;
    title: string;
    text: string;
    textFR: string;
    category: string;
  };
  goalActionStatus: Array<{
    id: string;
    isDone: boolean;
    GoalAction: {
      id: string;
      title: string;
    };
  }>;
};

type Props = {
  goalData: GoalData;
  isPeerReview?: boolean; // To determine color scheme
  isHRView?: boolean; // For HR Analytics view
};

export const GoalDetailsCard = ({ goalData, isPeerReview = false, isHRView = false }: Props) => {
  const { translate } = useLocales();
  const theme = useTheme();

  // Determine color scheme
  const isBlueTheme = isPeerReview && !isHRView;
  const primaryColor = isBlueTheme ? theme.palette.secondary.main : theme.palette.primary.main;
  const primaryLight = isBlueTheme ? theme.palette.secondary.light : theme.palette.primary.light;

  // Check if goal is completed
  const isCompleted = !!goalData.dateCompleted;

  // Create gradient background based on completion status (same logic as SimpleGoalCard)
  const gradientBackground = isCompleted
    ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
    : isBlueTheme 
      ? `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`
      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  // Get category icon and tooltip
  const category = goalData.goalObjective.category as QuestionCategory;
  const categoryTooltips = getCategoryTooltips(translate);
  const categoryIcon = categoryTooltips[category] || CATEGORY_ICONS[category];

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        p: 0,
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        backgroundColor: theme.palette.background.paper,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header Section with Gradient */}
      <Box
        sx={{
          p: 3,
          background: gradientBackground,
          color: "white",
        }}
      >
        {/* Category at the top */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {categoryIcon}
          <Chip
            label={goalData.goalObjective.category}
            size="small"
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              fontWeight: "bold",
              fontSize: "0.75rem",
            }}
          />
        </Box>

        {/* Title/Question */}
        <Typography variant="h6" fontWeight="bold">
          {goalData.goalObjective.title}
        </Typography>
      </Box>

      {/* Status Section */}
      <Box p={2} borderBottom={`1px solid ${theme.palette.divider}`}>
        <Stack 
          direction={{ xs: "column", sm: "row" }} 
          spacing={{ xs: 2, sm: 3 }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          {/* Completion Status */}
          <Box display="flex" alignItems="center" gap={1} minWidth="fit-content">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: goalData.dateCompleted ? theme.palette.success.main : theme.palette.warning.main,
              }}
            />
            <Typography variant="body2" fontWeight="medium">
              {goalData.dateCompleted 
                ? `${translate("hr_analytics.completed_on") as unknown as string} ${new Date(goalData.dateCompleted).toLocaleDateString()}`
                : (translate("hr_analytics.not_completed") as unknown as string)}
            </Typography>
          </Box>
          
          {/* Target Date */}
          <Box minWidth="fit-content">
            <Typography variant="body2" color="text.secondary">
              {translate("hr_analytics.due_label") as unknown as string}: {new Date(goalData.targetDate).toLocaleDateString()}
            </Typography>
          </Box>
          
          {/* Target Rating */}
          <Box minWidth="fit-content">
            <Typography variant="body2" color="text.secondary">
              {translate("hr_analytics.rating_label") as unknown as string}: {goalData.targetRating}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Actions Section */}
      {goalData.goalActionStatus?.length > 0 && (
        <Box p={2}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {translate("hr_analytics.actions") as unknown as string}
          </Typography>
          <Stack spacing={1}>
            {goalData.goalActionStatus.map((action) => (
              <Box
                key={action.id}
                display="flex"
                alignItems="center"
                gap={2}
                p={1.5}
                sx={{
                  backgroundColor: action.isDone 
                    ? alpha(primaryColor, 0.1) 
                    : alpha(theme.palette.grey[500], 0.05),
                  borderRadius: 2,
                  border: `1px solid ${action.isDone 
                    ? alpha(primaryColor, 0.3) 
                    : alpha(theme.palette.grey[500], 0.2)}`,
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: action.isDone ? primaryColor : theme.palette.grey[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "12px",
                    fontWeight: "bold",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {action.isDone ? "✓" : ""}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    color: action.isDone ? primaryColor : "text.primary",
                    fontWeight: action.isDone ? 500 : 400,
                  }}
                >
                  {action.GoalAction.title}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  );
};